import { Link } from "react-router-dom";
import { CheckCircle2, PlayCircle, HelpCircle, Flag, Lock } from "lucide-react";

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => a.order - b.order);

// Sidebar du curriculum partagée entre la page leçon et la page quizz.
// active : { type: "lesson" | "quiz", id }
const CurriculumSidebar = ({ formation, progress, active }) => {
  const modules = sortByOrder(formation.FormationModules);
  const orderedLessons = modules.flatMap((m) => sortByOrder(m.FormationLessons));
  const completedSet = new Set(progress.completedLessonIds || []);

  const quizzes = formation.FormationQuizzes || [];
  const finalQuiz = quizzes.find((q) => !q.moduleId);
  const moduleQuiz = (mid) => quizzes.find((q) => q.moduleId === mid);
  // Un module sans leçon est ignoré dans le déverrouillage (complet par convention),
  // même règle que le backend : il ne bloque jamais le quizz final.
  const isModuleComplete = (m) => {
    const ls = m.FormationLessons || [];
    return ls.length === 0 || ls.every((l) => completedSet.has(l.id));
  };
  const allModulesComplete = modules.length > 0 && modules.every(isModuleComplete);
  const quizPassed = (qid) => !!(progress.quizState && progress.quizState[qid] && progress.quizState[qid].passed);
  const percent = orderedLessons.length
    ? Math.round((completedSet.size / orderedLessons.length) * 100)
    : 0;

  const isActive = (type, eid) => active && active.type === type && String(active.id) === String(eid);

  return (
    <aside className="lesson-side">
      <Link to={`/formations/${formation.id}`} className="lesson-back">
        {formation.title}
      </Link>

      <div className="lesson-progress">
        <div className="lesson-progress-bar">
          <span style={{ width: `${percent}%` }} />
        </div>
        <span className="lesson-progress-label">{percent}%</span>
      </div>

      {modules.map((m) => {
        const mComplete = isModuleComplete(m);
        const mq = moduleQuiz(m.id);
        return (
          <div className="lesson-side-module" key={m.id}>
            <p className="lesson-side-mtitle">{m.title}</p>
            {sortByOrder(m.FormationLessons).map((l) => (
              <Link
                key={l.id}
                to={`/formations/${formation.id}/apprendre/${l.id}`}
                className={`lesson-side-lesson ${isActive("lesson", l.id) ? "active" : ""}`}
              >
                {completedSet.has(l.id) ? (
                  <CheckCircle2 size={15} className="done" />
                ) : (
                  <PlayCircle size={15} />
                )}
                {l.title}
              </Link>
            ))}
            {mq ? (
              mComplete ? (
                <Link
                  to={`/formations/${formation.id}/quizz/${mq.id}`}
                  className={`lesson-side-lesson quiz ${isActive("quiz", mq.id) ? "active" : ""}`}
                >
                  {quizPassed(mq.id) ? (
                    <CheckCircle2 size={15} className="done" />
                  ) : (
                    <HelpCircle size={15} />
                  )}
                  Quizz du module
                </Link>
              ) : (
                <span className="lesson-side-lesson locked" title="Terminez les leçons du module pour débloquer le quizz">
                  <Lock size={15} /> Quizz du module
                </span>
              )
            ) : null}
          </div>
        );
      })}

      {finalQuiz ? (
        allModulesComplete ? (
          <Link
            to={`/formations/${formation.id}/quizz/${finalQuiz.id}`}
            className={`lesson-side-lesson quiz final ${isActive("quiz", finalQuiz.id) ? "active" : ""}`}
          >
            {quizPassed(finalQuiz.id) ? <CheckCircle2 size={15} className="done" /> : <Flag size={15} />}
            Quizz final
          </Link>
        ) : (
          <span className="lesson-side-lesson locked final" title="Terminez tous les modules pour débloquer le quizz final">
            <Lock size={15} /> Quizz final
          </span>
        )
      ) : null}
    </aside>
  );
};

export default CurriculumSidebar;
